import { modifier } from 'ember-modifier';
import { throttle } from '@ember/runloop';

interface ToggleScrollClassSignature {
  Element: Element;
  Args: {
    Positional: [string?, string?];
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'toggle-scroll-class': typeof toggleScrollClassModifier;
  }
}

/**
 * This modifier adds a class (`is-scrolled`) to the associated element when it
 * has been scrolled horizontally.
 *
 * i.e. <MyComponent {{toggle-scroll-class}} />
 */
const toggleScrollClassModifier = modifier<ToggleScrollClassSignature>(
  function toggleScrollClass(element, positional) {
    const verticalClassName = positional?.[0];
    const horizontalClassName = positional?.[1];
    const handleScrollThrottled = (/* e: Event */) => {
      // eslint-disable-next-line ember/no-runloop
      throttle(
        handleScroll,
        element,
        verticalClassName,
        horizontalClassName,
        25
      );
    };
    const handleScrollImmediate = (/* e: Event */) =>
      handleScroll(element, verticalClassName, horizontalClassName);

    // throttle scroll event so it doesn't run too often when scrolling
    element?.addEventListener('scroll', handleScrollThrottled);
    // do not throttle scrollend since it only runs once at the end
    element?.addEventListener('scrollend', handleScrollImmediate);

    return () => {
      element?.removeEventListener('scroll', handleScrollThrottled);
      element?.removeEventListener('scrollend', handleScrollImmediate);
    };
  }
);

function handleScroll(
  element: Element,
  verticalClassName = 'is-scrolled-v',
  horizontalClassName = 'is-scrolled-h'
) {
  if (element.scrollTop > 0) {
    element.classList.add(verticalClassName);
  } else {
    element.classList.remove(verticalClassName);
  }

  if (element.scrollLeft > 0) {
    element.classList.add(horizontalClassName);
  } else {
    element.classList.remove(horizontalClassName);
  }
}

export default toggleScrollClassModifier;
